import React from "react";
import { useLocation } from "react-router-dom";
import { Mail, Phone, MapPin, Clock, Facebook } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useSEO, breadcrumbJsonLd, siteUrl } from "@/lib/seo";
import ContactForm from "@/components/ContactForm";

const ALLOWED_TYPES = ["quote", "demo", "contact"];

const Contact = () => {
  const { lang } = useLang();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const requestedType = params.get("type");
  const defaultType = ALLOWED_TYPES.includes(requestedType) ? requestedType : "contact";

  useSEO({
    title: lang === "nl"
      ? "Contact — Wasz.nl Mobiele Truckwash"
      : "Contact — Wasz.nl Mobile Truckwash",
    description: lang === "nl"
      ? "Neem direct contact op met Wasz.nl voor mobiele truckwash, Fleet Cleaning of een WaszBase demo. Bel 036 525 5021 of mail info@wasz.nl."
      : "Get in touch with Wasz.nl for mobile truckwash, fleet cleaning or a WaszBase demo. Call +31 36 525 5021 or email info@wasz.nl.",
    canonical: siteUrl("/contact"),
    lang,
    jsonLd: [
      {
        id: "contact-breadcrumb",
        data: breadcrumbJsonLd([
          { name: "Home", url: siteUrl("/") },
          { name: "Contact", url: siteUrl("/contact") },
        ]),
      },
    ],
  });

  return (
    <main data-testid="contact-page" className="min-h-screen pt-20 pb-24">
      <section className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="max-w-2xl mb-14">
          <p className="section-overline mb-3">Contact</p>
          <h1 className="text-4xl sm:text-6xl font-black tracking-tighter text-white leading-[1.05]">
            {lang === "nl" ? (
              <>Vraag een offerte, demo of <span className="text-[#00D4FF]">stel uw vraag</span>.</>
            ) : (
              <>Request a quote, demo or <span className="text-[#00D4FF]">ask a question</span>.</>
            )}
          </h1>
          <p className="mt-5 text-white/60 text-lg max-w-xl leading-relaxed">
            {lang === "nl"
              ? "Eén formulier, drie aanvraagtypes. We reageren binnen 1 werkdag — meestal sneller."
              : "One form, three request types. We respond within one business day — usually faster."}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <ContactForm dataTestid="contact-form" defaultType={defaultType} />
          </div>

          <aside className="lg:col-span-5 space-y-5">
            <div className="rounded-2xl border border-white/10 bg-[#0F0F23] p-7">
              <h3 className="text-xl font-bold text-white mb-5">
                {lang === "nl" ? "Direct contact" : "Direct contact"}
              </h3>
              <ul className="space-y-4 text-sm text-white/70">
                <li className="flex items-start gap-3">
                  <Phone className="w-4 h-4 text-[#00D4FF] mt-0.5 shrink-0" />
                  <div>
                    <div className="text-white/40 text-[11px] uppercase tracking-wider mb-1">
                      {lang === "nl" ? "Telefoon" : "Phone"}
                    </div>
                    <a href="tel:0365255021" data-testid="contact-phone" className="text-white hover:text-[#00D4FF] font-medium">
                      036 525 5021
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Mail className="w-4 h-4 text-[#00D4FF] mt-0.5 shrink-0" />
                  <div>
                    <div className="text-white/40 text-[11px] uppercase tracking-wider mb-1">E-mail</div>
                    <a href="mailto:info@wasz.nl" data-testid="contact-email" className="text-white hover:text-[#00D4FF] font-medium">
                      info@wasz.nl
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-[#00D4FF] mt-0.5 shrink-0" />
                  <div>
                    <div className="text-white/40 text-[11px] uppercase tracking-wider mb-1">
                      {lang === "nl" ? "Postadres" : "Postal address"}
                    </div>
                    <div className="text-white">Scharstraat 25</div>
                    <div className="text-white/60 text-sm">1317 PL Almere</div>
                    <div className="text-white/45 text-xs mt-1.5 italic">
                      {lang === "nl"
                        ? "Wij zijn 100% mobiel — alle wasbeurten vinden plaats op uw locatie, op afspraak."
                        : "We operate 100% mobile — all washes take place at your location, by appointment."}
                    </div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Clock className="w-4 h-4 text-[#00D4FF] mt-0.5 shrink-0" />
                  <div>
                    <div className="text-white/40 text-[11px] uppercase tracking-wider mb-1">
                      {lang === "nl" ? "Bereikbaar" : "Office hours"}
                    </div>
                    <div className="text-white">Ma–Vr 07:00–19:00</div>
                    <div className="text-white/60 text-sm">{lang === "nl" ? "Operationeel ook in weekends." : "Operational also weekends."}</div>
                  </div>
                </li>
              </ul>
            </div>

            <div className="rounded-2xl border border-white/10 bg-[#0F0F23] p-7">
              <h3 className="text-base font-semibold text-white mb-3">
                {lang === "nl" ? "Liever even bellen?" : "Prefer to call?"}
              </h3>
              <p className="text-white/55 text-sm leading-relaxed">
                {lang === "nl"
                  ? "Omdat wij altijd onderweg of op locatie zijn, kunnen we niet altijd direct opnemen. Krijgt u geen gehoor? Spreek de voicemail in of stuur een e-mail — we bellen u dezelfde werkdag terug."
                  : "Because we are always on the road or on-site, we cannot always answer immediately. No answer? Leave a voicemail or send an e-mail — we will call you back the same business day."}
              </p>
              <a href="tel:0365255021" className="mt-5 btn-primary w-full justify-center" data-testid="contact-call-now">
                <Phone className="w-4 h-4" /> 036 525 5021
              </a>
              <a
                href="https://www.facebook.com/Wasz.nl"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-facebook"
                aria-label="Wasz.nl op Facebook"
                className="mt-3 inline-flex items-center justify-center gap-2 w-full h-10 rounded-md border border-white/10 text-white/65 hover:text-[#00D4FF] hover:border-[#00D4FF]/40 text-xs font-semibold tracking-wider uppercase transition-colors"
              >
                <Facebook className="w-3.5 h-3.5" strokeWidth={1.8} />
                {lang === "nl" ? "Volg ons op Facebook" : "Follow us on Facebook"}
              </a>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
};

export default Contact;
